import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../config/supabase';
import { useNavigate } from 'react-router-dom';
import AdminRoute from '../../components/AdminRoute';
import { ArrowLeftIcon } from '../../components/icons/NavIcons';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageHeader from '../../components/PageHeader';

const CategoriesManagementPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingColor, setEditingColor] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
      alert(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleColorChange = (categoryId, newColor) => {
    setEditingId(categoryId);
    setEditingColor(newColor);
  };

  const saveColor = async (categoryId) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('categories')
        .update({ 
          color_code: editingColor,
          updated_at: new Date().toISOString()
        })
        .eq('id', categoryId);

      if (error) throw error;

      // Mettre à jour l'état local
      setCategories(categories.map(cat => 
        cat.id === categoryId ? { ...cat, color_code: editingColor } : cat
      ));

      setEditingId(null);
      setEditingColor('');
      
      // Afficher un message de succès temporaire
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingColor('');
  };

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  if (loading) {
    return (
      <div className="p-4">
        <LoadingSpinner message={t('common.loading') + '...'} />
      </div>
    );
  }

  return (
    <AdminRoute>
      <div className="min-h-screen bg-gray-50 pb-20">
        <PageHeader title={t('settings.admin.categories.title')} backPath="/settings" />

        <div className="p-4">
          {/* Message de succès */}
          {saveSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <span className="text-green-600">✓</span>
              <span className="text-sm text-green-700">{t('common.saved')}</span>
            </div>
          )}

          {/* Vue en grille compacte */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {categories.map((category) => {
              const currentColor = category.color_code || '#000000';
              
              return (
                <button
                  key={category.id}
                  onClick={() => {
                    setEditingId(category.id);
                    setEditingColor(currentColor);
                  }}
                  className={`bg-white rounded-lg shadow-sm p-3 transition-all ${
                    editingId === category.id ? 'ring-2 ring-[#2894D8]' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {category.icon && (
                      <span className="text-xl">{category.icon}</span>
                    )}
                    <div
                      className="w-8 h-8 rounded border-2 border-gray-200 flex-shrink-0"
                      style={{ backgroundColor: currentColor }}
                    />
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium truncate">{category.name}</p>
                      <p className="text-xs text-gray-500 font-mono">{currentColor}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Éditeur de couleur */}
          {editingId && (
            <div className="bg-white rounded-lg shadow-lg p-4 sticky bottom-4">
              {(() => {
                const category = categories.find(c => c.id === editingId);
                const currentColor = editingColor || category?.color_code || '#000000';
                const rgb = hexToRgb(currentColor);
                
                return (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        {category?.icon && (
                          <span className="text-2xl">{category.icon}</span>
                        )}
                        <h3 className="font-medium">{category?.name}</h3>
                      </div>
                      <button
                        onClick={cancelEdit}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        ✕
                      </button>
                    </div>

                    <div className="space-y-3">
                      {/* Palette de couleurs */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('settings.admin.categories.colorPicker')}
                        </label>
                        <input
                          type="color"
                          value={currentColor}
                          onChange={(e) => handleColorChange(editingId, e.target.value)}
                          className="w-full h-12 rounded border border-gray-300 cursor-pointer"
                        />
                      </div>

                      {/* Code hexadécimal */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('settings.admin.categories.hexCode')}
                        </label>
                        <input
                          type="text"
                          value={currentColor}
                          onChange={(e) => handleColorChange(editingId, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                          placeholder="#000000"
                          pattern="^#[0-9A-Fa-f]{6}$"
                          maxLength={7}
                        />
                      </div>

                      {/* Code RGB */}
                      {rgb && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('settings.admin.categories.rgbCode')}
                          </label>
                          <div className="px-3 py-2 bg-gray-50 rounded-lg font-mono text-sm text-gray-600">
                            rgb({rgb.r}, {rgb.g}, {rgb.b})
                          </div>
                        </div>
                      )}

                      {/* Boutons d'action */}
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={() => saveColor(editingId)}
                          disabled={saving}
                          className="flex-1 bg-[#2894D8] text-white py-2 rounded-lg hover:bg-[#2380c0] disabled:opacity-50"
                        >
                          {saving ? t('common.loading') : t('common.save')}
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={saving}
                          className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </AdminRoute>
  );
};

export default CategoriesManagementPage;
